import { GetStaticPaths, GetStaticProps } from 'next';
import dynamic from 'next/dynamic';
import slugify from 'slugify';

import client from '@utils/apolloClient';
import {
  countReviewItems,
  getReviewItemBySlug,
  getReviewSlugs,
} from 'queries/reviews.graphql';

import getAllStorySlugs from '@utils/getAllStorySlugs';
import getAvailabilty from '@utils/getAvailability';
import getEntryBoxInfo from '@utils/getEntryBoxInfo';
import getFullUrl from '@utils/getFullUrl';
import getStoreInfo from '@utils/getStoreInfo';
import getStructuredData from '@utils/getStructuredData';
import replaceImageHost from '@utils/replaceImageHost';

import Article from '@components/Article/Article';
import Layout from '@components/Layout/Layout';
import MetaDetails from '@components/MetaDetails/MetaDetails';
import Summary, { RatingsProps } from '@components/Summary/Summary';
import getImagePath from '@utils/getImagePath';
import sortBy from '@utils/sortBy';
import { TagList } from '@components/Tags/Tags';

const AffliateMessage = dynamic(
  () => import('@components/AffiliateMessage/AffliateMessage')
);
const Banner = dynamic(() => import('@components/Banner/Banner'));
const ContentList = dynamic(
  () => import('@components/ContentList/ContentList')
);
const Description = dynamic(
  () => import('@components/Description/Description')
);
const ImageGallery = dynamic(
  () => import('@components/ImageGallery/ImageGallery')
);
const InfoBox = dynamic(() => import('@components/InfoBox/InfoBox'));
const ProductInfoPurchase = dynamic(
  () =>
    import('@components/ProductInfoBox/ProductInfoPurchase/ProductInfoPurchase')
);
const ProductMeta = dynamic(
  () => import('@components/ProductMeta/ProductMeta')
);
const SecondaryTitle = dynamic(
  () => import('@components/SecondaryTitle/SecondaryTitle')
);
const Social = dynamic(() => import('@components/Social/Social'));
const Strapline = dynamic(() => import('@components/Strapline/Strapline'));
const Tags = dynamic(() => import('@components/Tags/Tags'));
const Title = dynamic(() => import('@components/Title/Title'));

type ReviewPostContent = {
  banner: any;
  body: string;
  build_text: string;
  build_value: string;
  conclusion: string;
  display_text: string;
  display_value: string;
  images1: any;
  images2: any;
  introduction: string;
  meta_description: string;
  play_text: string;
  play_value: string;
  recommended: boolean;
  strapline: string;
  summary: string;
  vfm_text: string;
  vfm_value: string;
};

interface ReviewsPostProps extends RatingsProps {
  content: ReviewPostContent;
  date: string;
  featureFlags: any;
  introduction: string;
  latest: any;
  product: any;
  schema: string;
  siteDescription: string;
  siteTitle: string;
  siteUrl: string;
  slug: string;
  tags?: TagList;
  title: string;
}

const ReviewsPost = ({
  content,
  date,
  featureFlags,
  latest,
  product,
  schema,
  siteDescription,
  siteTitle,
  siteUrl,
  slug,
  tags,
  title,
}: ReviewsPostProps) => {
  const {
    banner,
    body,
    build_text,
    build_value,
    conclusion,
    display_text,
    display_value,
    images1,
    images2,
    introduction,
    meta_description,
    play_text,
    play_value,
    recommended,
    strapline,
    summary,
    vfm_text,
    vfm_value,
  } = content;
  const section = 'Reviews';
  const pageTitle = `${title} Review`;

  const url = getFullUrl(section, slug);

  const brand = product.content && JSON.parse(product.content.brand || {});
  const releases = (product.content && product?.content.releases) || [];
  const stores = (product.content && product?.content.stores) || [];

  const releaseStatus = getAvailabilty(releases);

  return (
    <Layout
      featureFlags={featureFlags}
      pageDescription={meta_description || siteDescription}
      pageTitle={title}
      pageType="article"
      schema={schema}
      section={section}
      siteTitle={siteTitle}
      url={url}
    >
      <Article>
        <Article.HeaderWrapper>
          <Title>{pageTitle}</Title>
          <Strapline text={strapline} />
        </Article.HeaderWrapper>

        <Article.ContentWrapper>
          <MetaDetails
            dt={date}
            dtLabel="Published"
            section={section}
          ></MetaDetails>

          {product.content && (
            <ProductMeta
              reference={product.content.ref}
              referenceName={brand.ref}
              parts={product.content.parts?.toString()}
              partsName={brand.part}
              figures={product.content.figures?.toString()}
              figuresName={brand.figure}
              releases={releases}
            ></ProductMeta>
          )}
        </Article.ContentWrapper>

        <Article.ContentWrapper>
          {introduction ? (
            <Description content={introduction} leadingLetter={true} />
          ) : null}
          {product.content && stores.length > 0 && (
            <>
              <InfoBox section={section.toLowerCase()}>
                <ProductInfoPurchase
                  brand={brand.title}
                  reference={product.content.ref}
                  releaseStatus={releaseStatus}
                  section={section}
                  stores={getStoreInfo(stores)}
                  title={title}
                ></ProductInfoPurchase>
              </InfoBox>
              <AffliateMessage />
            </>
          )}

          {body ? <Description content={body} /> : null}
          {images1 ? <ImageGallery images={images1} /> : null}
        </Article.ContentWrapper>

        {conclusion ? (
          <Article.ContentWrapper>
            <Description content={conclusion} />
          </Article.ContentWrapper>
        ) : null}

        {images2 ? <ImageGallery images={images2} /> : null}

        <Article.ContentWrapper>
          <Summary
            build_text={build_text}
            build_value={build_value}
            display_text={display_text}
            display_value={display_value}
            play_text={play_text}
            play_value={play_value}
            recommended={recommended}
            section={section}
            siteTitle={siteTitle}
            summary={summary}
            vfm_text={vfm_text}
            vfm_value={vfm_value}
          />
          {product.content && stores.length > 0 && (
            <InfoBox section={section.toLowerCase()}>
              <ProductInfoPurchase
                brand={brand.title}
                reference={product.content.ref}
                releaseStatus={releaseStatus}
                section={section}
                stores={getStoreInfo(stores)}
                title={title}
              ></ProductInfoPurchase>
            </InfoBox>
          )}

          <Social title={title} url={url} />
          {tags ? <Tags tags={tags} /> : null}
        </Article.ContentWrapper>

        <SecondaryTitle>Latest Reviews</SecondaryTitle>
        <ContentList limit={true} posts={latest || []} />
        {banner && banner.url && <Banner image={banner}></Banner>}
      </Article>
    </Layout>
  );
};

export default ReviewsPost;

export const getStaticProps: GetStaticProps = async ({ ...ctx }) => {
  const { review: slug } = ctx.params as { review: string };

  const configData = await import(`../../siteconfig.json`);

  const {
    data: { ReviewItem: review, ReviewItems: latest },
  } = await client.query({
    query: getReviewItemBySlug,
    variables: {
      slug: `reviews/${slug}`,
    },
  });

  if (!review) {
    throw new Error('Not found');
  }

  const productDetails = review.content.product;

  const schema = getStructuredData({
    title: review.title,
    date: review.date,
    image: review.content.hero.url,
  });

  return {
    props: {
      ...review,
      content: {
        ...review.content,
        banner: review.content.banner?.url
          ? {
              ...review.content.banner,
              url: getImagePath({ url: review.content.banner.url }),
            }
          : null,
        body: replaceImageHost(review.content.body),
        conclusion: replaceImageHost(review.content.conclusion),
        hero: null,
        images1: review.content.images1.map((image) => ({
          ...image,
          url: {
            blur: getImagePath({
              dimensions: '30x0',
              fitBackground: '#ffffff',
              quality: 75,
              url: image.url,
            }),
            full: getImagePath({
              dimensions: '640x480',
              fitBackground: '#ffffff',
              quality: 75,
              url: image.url,
            }),
          },
        })),
        images2: review.content.images2.map((image) => ({
          ...image,
          url: {
            blur: getImagePath({
              dimensions: '30x0',
              fitBackground: '#ffffff',
              quality: 75,
              url: image.url,
            }),
            full: getImagePath({
              dimensions: '640x0',
              fitBackground: '#ffffff',
              quality: 75,
              url: image.url,
            }),
          },
        })),
        introduction: replaceImageHost(review.content.introduction),
      },
      featureFlags: configData.featureFlags,
      latest: latest.items
        .filter((item) => item.slug !== review.slug)
        .map((item) => getEntryBoxInfo(item, 'Reviews')),
      product: productDetails,
      schema,
      siteDescription: configData.default.description,
      siteTitle: configData.default.title,
      siteUrl: configData.url,
      tags: sortBy(
        'name',
        review.tag_list.map((tag) => ({
          name: tag,
          slug: slugify(tag, { lower: true }),
        }))
      ),
    },
  };
};

export const getStaticPaths: GetStaticPaths = async () => {
  type Reviews = Array<{
    slug: string;
  }>;

  const reviews: Reviews = await getAllStorySlugs(
    'ReviewItems',
    countReviewItems,
    getReviewSlugs
  );

  const paths = reviews.map((review) => `/reviews/${review.slug}`);

  return {
    paths,
    fallback: false,
  };
};
